import React from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import { defineMessages, FormattedMessage, intlShape } from 'react-intl'

// Constants
import { FORM_FIELD_NAMES } from 'src/constants/affordability'
import { LOAN_TERMS } from 'src/constants/loan'

// Components
import FormField from 'src/Components/shared/FormField'
import DollarInput from 'src/Components/shared/Input/DollarInput'
import Text from 'src/Components/shared/Text'
import Select from 'src/Components/shared/Select'
import PersistAffordabilityButton from 'src/Components/buyers/Global/AffordabilityModal/PersistAffordabilityButton'
import PriceCounter from './PriceCounter'

// Styles
import styles from './CalculatePricePointTab.module.scss'

const StyledText = ({ className, ...restProps }) => (
  <Text
    variant='neutralCool'
    variantCount='900'
    element='span'
    className={classnames(styles.text, className)}
    {...restProps}
  />
)

const Caption = props => <Text size='xs' variant='neutralWarm' {...props} />

const StyledInput = props => <DollarInput className={styles.input} {...props} />

const StyledSelect = props => <Select className={styles.select} {...props} />

const StyledDollarsField = props => (
  <FormField.Dollars
    input={StyledInput}
    inputClassName={styles.styledInput}
    labelClass={styles.label}
    labelText={StyledText}
    required
    {...props}
  />
)

const loanTermMessages = defineMessages({
  value: {
    id: 'Affordability.form.loanTermDropdownValue',
    defaultMessage: '{loanTermYears, number}-year',
  },
})

const termOptions = intl =>
  LOAN_TERMS.map(term => ({
    key: term,
    value: intl.formatMessage(loanTermMessages.value, {
      loanTermYears: term,
    }),
  }))

const CalculatePricePointTab = ({
  onChange,
  income,
  debt,
  downPayment,
  loanTerm,
  intl,
}) => (
  <>
    <StyledDollarsField
      data-qa='inputIncome'
      name={FORM_FIELD_NAMES.INCOME}
      onChange={onChange}
      value={income}
    >
      <FormattedMessage
        id='Affordability.form.incomeLabel'
        defaultMessage='Annual household income'
      />
      <Caption>
        <FormattedMessage
          id='Affordability.form.incomeSublabel'
          defaultMessage='The total combined income of every adult in the household'
        />
      </Caption>
    </StyledDollarsField>

    <StyledDollarsField
      data-qa='inputDebt'
      name={FORM_FIELD_NAMES.DEBT}
      onChange={onChange}
      value={debt}
    >
      <FormattedMessage
        id='Affordability.form.debtLabel'
        defaultMessage='Monthly debt you pay'
      />
      <Caption>
        <FormattedMessage
          id='Affordability.form.debtSublabel'
          defaultMessage='Car payments, credit card payments, other non-rent or household debt'
        />
      </Caption>
    </StyledDollarsField>

    <StyledDollarsField
      data-qa='inputDownPayment'
      name={FORM_FIELD_NAMES.DOWNPAYMENT}
      onChange={onChange}
      value={downPayment}
    >
      <FormattedMessage
        id='Affordability.form.downPaymentLabel'
        defaultMessage='Down payment'
      />
      <Caption>
        <FormattedMessage
          id='Affordability.form.downPaymentSublabel'
          defaultMessage='The amount of cash you can put down on the house at closing.'
        />
      </Caption>
    </StyledDollarsField>

    <FormField
      data-qa='selectLoanTerm'
      input={StyledSelect}
      inputClassName={styles.inputLoanDropdown}
      labelText={StyledText}
      labelClass={styles.label}
      name={FORM_FIELD_NAMES.LOANTERM}
      options={termOptions(intl)}
      onChange={value =>
        onChange(
          {
            target: {
              name: FORM_FIELD_NAMES.LOANTERM,
            },
          },
          value
        )
      }
      value={loanTerm}
    >
      <FormattedMessage
        id='Affordability.form.loanTermLabel'
        defaultMessage='Loan term'
      />
      <Caption>
        <FormattedMessage
          id='Affordability.form.loanTermSublabel'
          defaultMessage='How long it will take to pay off your new house'
        />
      </Caption>
    </FormField>

    <PriceCounter />

    <PersistAffordabilityButton className={styles.button} />
  </>
)

CalculatePricePointTab.propTypes = {
  onChange: PropTypes.func,
  income: PropTypes.number,
  debt: PropTypes.number,
  downPayment: PropTypes.number,
  loanTerm: PropTypes.number,
  intl: intlShape,
}

StyledText.propTypes = {
  className: PropTypes.string,
}

export default CalculatePricePointTab
